import React from 'react'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { TalentAnalyticsDocument } from 'generated/resources/graphql'
import { keyBy } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { isModuleAvailabilityEnabled } from 'modules/core/lib/isModuleAvailabilityEnabled'
import { buildModuleRoute, buildNavigation } from 'modules/core/lib/react-router'

const RedirectToDefaultPage = React.lazy(() => import('../pages/RedirectToDefaultPage'))
const AnalitycsPage = React.lazy(() => import('../pages/analytics'))
const RootPage = React.lazy(() => import('../pages/RootPage'))

export const talentAnalyticsRoutes = buildNavigation((navigation) => [
  buildModuleRoute({
    path: navigation.talentAnalytics.path,
    element: <RootPage />,
    handle: {
      module: 'talent_analytics',
      moduleName: () => i18n.t('sidebar.talent_analytics'),
      meta: ({ watchQuery, navigate, session }) => {
        return watchQuery({
          query: TalentAnalyticsDocument,
          variables: {
            companyId: session.company.id,
            moduleAvailabilityIds: [AvailableModules.review_process],
            featureAvailabilityIds: [Features.engagement_employee_pulse_questions],
          },
        }).map(({ data }) => {
          if (!data.performance._policies.sidebarSeeAnalyticsSubLevelItem.can) {
            navigate(navigation.dashboard.fullPath())
          }
          const features = keyBy(data?.features?.featuresConnection?.nodes, 'name')

          const employeePulseFFEnabled =
            features['engagement_employee_pulse_questions']?.enabled ?? false

          const employeePulseEnabled =
            isFeatureAvailabilityEnabled(
              data?.companies?.featureAvailabilitiesConnection?.nodes || [],
              Features.engagement_employee_pulse_questions
            ) || employeePulseFFEnabled

          const moduleAvailabilities = data?.companies?.moduleAvailabilitiesConnection?.nodes

          const canRequestUpsell = data?.upsell?._policies?.canRequestUpsell?.can ?? false

          const hasPerformance =
            moduleAvailabilities &&
            isModuleAvailabilityEnabled(moduleAvailabilities, AvailableModules.review_process)

          const showPerformanceUpsell = canRequestUpsell && !hasPerformance

          return {
            title: i18n.t('sidebar.talent_analytics'),
            subNavigation: [
              {
                title: i18n.t('performance_process.analytics.chart_type.headcount'),
                href: navigation.talentAnalytics.headcount.fullPath(),
                id: navigation.talentAnalytics.headcount.fullPath(),
              },
              {
                title: i18n.t('performance_process.analytics.chart_type.employee_pulse'),
                href: navigation.talentAnalytics.pulseSurvey.fullPath(),
                id: navigation.talentAnalytics.pulseSurvey.fullPath(),
                isVisible: employeePulseEnabled,
              },
              {
                title: i18n.t('performance_process.analytics.chart_type.performance'),
                href: navigation.talentAnalytics.performance.fullPath(),
                id: navigation.talentAnalytics.performance.fullPath(),
                isVisible: hasPerformance || canRequestUpsell,
                variant: showPerformanceUpsell ? 'upsell' : undefined,
              },
              {
                title: i18n.t('performance_process.analytics.chart_type.9_box_grid'),
                href: navigation.talentAnalytics.nineBoxGrid.fullPath(),
                id: navigation.talentAnalytics.nineBoxGrid.fullPath(),
                isVisible: hasPerformance || canRequestUpsell,
                variant: showPerformanceUpsell ? 'upsell' : undefined,
              },
            ],
          }
        })
      },
    },
    children: [
      {
        index: true,
        element: <RedirectToDefaultPage />,
      },
      {
        path: navigation.talentAnalytics.headcount.path,
        element: <AnalitycsPage />,
      },
      {
        path: navigation.talentAnalytics.performance.path,
        element: <AnalitycsPage />,
      },
      {
        path: navigation.talentAnalytics.nineBoxGrid.path,
        element: <AnalitycsPage />,
      },
      {
        path: navigation.talentAnalytics.pulseSurvey.path,
        element: <AnalitycsPage />,
      },
    ],
  }),
])
